import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Settings, User, Building, Globe, AlertTriangle, RotateCcw, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user, organization, token, refreshOrganization } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resetStep, setResetStep] = useState(0); // 0=idle, 1=first confirm, 2=second confirm
  const [backupInfo, setBackupInfo] = useState(null);
  const [orgData, setOrgData] = useState({
    name: organization?.name || '',
    tournament_name: organization?.tournament_name || '',
    primary_color: organization?.branding?.primary_color || '#10b981'
  });

  const handleSaveOrg = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/organizations/${organization.org_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: orgData.name,
          tournament_name: orgData.tournament_name,
          branding: {
            ...organization.branding,
            primary_color: orgData.primary_color
          }
        })
      });

      if (!response.ok) throw new Error('Failed to update settings');
      
      await refreshOrganization();
      toast.success('Settings saved!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="settings-page" className="space-y-8 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
            Settings
          </h1>
          <p className="text-gray-600 mt-2">Manage your account and organization settings</p>
        </div>

        {/* Profile Section */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="border-b border-gray-200">
            <CardTitle className="font-heading text-xl text-black uppercase flex items-center gap-2">
              <User className="text-emerald-500" size={20} />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
              ) : (
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <span className="text-emerald-500 text-2xl font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}
              <div>
                <p className="text-black font-medium text-lg">{user?.name}</p>
                <p className="text-gray-500">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization Settings */}
        {organization && (
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="font-heading text-xl text-black uppercase flex items-center gap-2">
                <Building className="text-emerald-500" size={20} />
                Organization
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSaveOrg} className="space-y-5">
                <div>
                  <Label className="text-gray-700">Organization Name</Label>
                  <Input
                    value={orgData.name}
                    onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                    className="mt-2 bg-gray-100 border-gray-200 text-black"
                    data-testid="org-name-setting"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Tournament Name</Label>
                  <Input
                    value={orgData.tournament_name}
                    onChange={(e) => setOrgData({ ...orgData, tournament_name: e.target.value })}
                    className="mt-2 bg-gray-100 border-gray-200 text-black"
                    data-testid="tournament-name-setting"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Primary Color</Label>
                  <div className="flex gap-3 mt-2">
                    <Input
                      type="color"
                      value={orgData.primary_color}
                      onChange={(e) => setOrgData({ ...orgData, primary_color: e.target.value })}
                      className="w-16 h-10 p-1 bg-gray-100 border-gray-200"
                    />
                    <Input
                      value={orgData.primary_color}
                      onChange={(e) => setOrgData({ ...orgData, primary_color: e.target.value })}
                      className="flex-1 bg-gray-100 border-gray-200 text-black"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black btn-lift"
                  data-testid="save-settings-btn"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Public URL */}
        {organization && (
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="font-heading text-xl text-black uppercase flex items-center gap-2">
                <Globe className="text-emerald-500" size={20} />
                Public Tournament Page
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600 text-sm mb-3">
                Share this link with fans to let them view standings and matches without login:
              </p>
              <div className="flex gap-3">
                <Input
                  readOnly
                  value={`${window.location.origin}/${organization.subdomain}`}
                  className="bg-gray-100 border-gray-200 text-emerald-500"
                />
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/${organization.subdomain}`);
                    toast.success('Link copied!');
                  }}
                  className="bg-gray-100 hover:bg-white/20 text-black"
                >
                  Copy
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recalculate Standings */}
        {organization && (
          <Card className="bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200">
              <CardTitle className="font-heading text-xl text-black uppercase flex items-center gap-2">
                <RefreshCw className="text-emerald-500" size={20} />
                Recalculate Standings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600 text-sm mb-4">
                If standings look incorrect, this will recalculate all team stats from completed matches only. 3 pts/win, 1 pt/draw, 0 pts/loss.
              </p>
              <Button
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/tournament/recalculate-standings`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!res.ok) throw new Error('Failed');
                    toast.success('Standings recalculated successfully');
                  } catch { toast.error('Failed to recalculate'); }
                }}
                className="bg-emerald-500 hover:bg-emerald-600 text-black"
                data-testid="recalculate-standings-btn"
              >
                <RefreshCw size={16} className="mr-2" />
                Recalculate Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tournament Reset - DANGER ZONE */}
        {organization && (
          <Card className="bg-white border-red-500/30">
            <CardHeader className="border-b border-red-500/20">
              <CardTitle className="font-heading text-xl text-red-400 uppercase flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={20} />
                Danger Zone — Tournament Reset
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-600 text-sm">
                This will reset ALL match scores to 0-0, statuses to &quot;Scheduled&quot;, and team stats to zero. 
                <strong className="text-black"> Players and schedule (dates/times/fields) are preserved.</strong> A backup is created and can be reverted within 28 hours.
              </p>

              {resetStep === 0 && (
                <Button
                  onClick={() => setResetStep(1)}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="reset-tournament-btn"
                >
                  <AlertTriangle size={16} className="mr-2" />
                  Reset Tournament Data
                </Button>
              )}

              {resetStep === 1 && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 space-y-3">
                  <p className="text-red-300 text-sm font-medium">
                    Are you sure? This will reset all scores, standings, and match statuses.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setResetStep(2)}
                      className="bg-red-600 hover:bg-red-700 text-black"
                      data-testid="reset-confirm-1"
                    >
                      Yes, I understand — Continue
                    </Button>
                    <Button
                      onClick={() => setResetStep(0)}
                      variant="ghost"
                      className="text-gray-600"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {resetStep === 2 && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 space-y-3">
                  <p className="text-red-200 text-sm font-bold">
                    FINAL CONFIRMATION — This action will reset your entire tournament. You have 28 hours to revert.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API_URL}/api/tournament/reset`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ confirmation: 'RESET_TOURNAMENT' })
                          });
                          if (!res.ok) throw new Error('Failed to reset');
                          const data = await res.json();
                          toast.success(data.message);
                          setResetStep(0);
                          setBackupInfo(data);
                        } catch (e) { toast.error(e.message); }
                      }}
                      className="bg-red-700 hover:bg-red-800 text-black"
                      data-testid="reset-confirm-2"
                    >
                      RESET TOURNAMENT NOW
                    </Button>
                    <Button
                      onClick={() => setResetStep(0)}
                      variant="ghost"
                      className="text-gray-600"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Revert from backup */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="text-gray-600 text-sm mb-3">
                  If you reset by mistake, you can revert to the last backup within 28 hours:
                </p>
                <Button
                  onClick={async () => {
                    if (!confirm('Revert tournament to last backup?')) return;
                    try {
                      const res = await fetch(`${API_URL}/api/tournament/revert`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!res.ok) {
                        const err = await res.json();
                        throw new Error(err.detail || 'Failed');
                      }
                      const data = await res.json();
                      toast.success(data.message);
                    } catch (e) { toast.error(e.message); }
                  }}
                  variant="outline"
                  className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  data-testid="revert-tournament-btn"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Revert to Last Backup
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
